import { Product } from 'src/services/searchProducts/dtos'
import { ProductApi } from 'src/types'
import {
  isValidColorBySlug,
  isValidVoltageBySlug,
} from 'src/util/productCombination'
import {
  getActiveSKUFromSearchProduct,
  getActiveSKUFromShowcaseProduct,
  getShowcaseProductCategoryId,
} from 'src/util/products'
import { ProductCombination, ProductProps } from './product-vertical'

const AVAILABLE = 'AVAILABLE'

const resolveColorsQuantity = (product: any): number => {
  if (product?.colors_quantity) {
    return product.colors_quantity
  }

  const cores = product?.details?.Cores

  if (Array.isArray(cores)) {
    return cores.length
  }

  if (typeof cores === 'string') {
    return cores.split(',').filter(Boolean).length
  }

  return 0
}

export const convertArraysToStringsOrNumbers = (obj: Product['details']) => {
  const anyObj = obj as any
  for (const key in anyObj) {
    if (Array.isArray(anyObj[key])) {
      const numericValue = anyObj[key].find(
        (val: any) => typeof val === 'number'
      )
      const isNotNumericAndDefined =
        numericValue === undefined && anyObj[key][0] !== undefined
      const floatValue = isNotNumericAndDefined
        ? parseFloat(anyObj[key][0].replace(',', '.'))
        : anyObj[key][0]

      anyObj[key] =
        numericValue ?? (isNaN(floatValue) ? anyObj[key][0] : floatValue)
    }
  }
  return anyObj
}

export const mapProductToAnalyticsProduct = (product: Product): Product => {
  const resultDetails = convertArraysToStringsOrNumbers(product.details)

  return {
    ...product,
    details: resultDetails,
    selectedSku: product.details.variacaoId?.[0],
    cId: getShowcaseProductCategoryId(product),
  }
}

export const mapProductApiToAnalyticsProduct = (
  product: ProductApi
): ProductApi => {
  const details = product.details
  return {
    ...product,
    selectedSku: Array.isArray(details.variacaoId)
      ? details.variacaoId[0]
      : details.variacaoId,
    cId: getShowcaseProductCategoryId(product),
  }
}

export const mapToItemProdutoVertical = (
  product: any,
  isMobile = true
): Record<string, any> => {
  const colorsQuantity = resolveColorsQuantity(product)

  const details = convertArraysToStringsOrNumbers(product.details) as any

  const color = makeSlugFromDetails(details, 'cor_id', 'Cor')
  const voltage = makeSlugFromDetails(details, 'voltagem_id', 'Voltagem')
  const productUrl = getActiveSKUFromSearchProduct(product)?.url || product.url

  const validatedTotalParcelado = Array.isArray(product.details.totalParcelado)
    ? product.details.totalParcelado[1]
    : product.details.totalParcelado

  const installmentPriceWithInterest =
    product.details.installmentPriceWithInterest ?? 0

  return {
    categories: product.categories,
    color,
    colorsQuantity,
    hasVariablePrice:
      product.has_variable_price ??
      product.details?.possuiPrecoVariavel ??
      false,
    de: product.oldPrice,
    id: product.id,
    imageUrl: product.images.default,
    imageWidth: isMobile ? 120 : 300,
    installmentPriceWithInterest,
    installmentMaxQuantity: product?.installment?.count || 0,
    installmentMaxQuantityWithoutInterest:
      product?.details?.numeroParcelasSemJuros,
    isAvailable: product.status.toUpperCase() === AVAILABLE,
    percent: product.details?.percentualDesconto,
    por: +validatedTotalParcelado,
    price: product.price,
    promoEventImage: {
      desktop: product.images?.promoEventDesktop,
      mobile: product.images?.promoEventMobile,
    },
    productUrl,
    rating: product.rating ?? details.mediaAvaliacoes ?? 0,
    reviewQuantity: details.quantidadeAvaliacoes ?? 0,
    reviewRating: details.mediaAvaliacoes ?? 0,
    showDiscountBadge: product.showDiscountBadge,
    showPromoBadge: product.showPromoBadge,
    skus: product.skus,
    title: product.name,
    trackingUrl: product.trackingUrl || product.clickUrl || undefined,
    voltage,
  }
}

export const mapProductApiToProductVerticalItem = (
  product: ProductApi
): ProductProps => {
  const details = product.details

  const colorsQuantity = resolveColorsQuantity(product)

  const color = makeSlugFromDetails(details, 'cor_id', 'Cor')
  const voltage = makeSlugFromDetails(details, 'voltagem_id', 'Voltagem')
  const productUrl =
    getActiveSKUFromShowcaseProduct(product)?.url || product.url

  const validatedTotalParcelado = Array.isArray(product.details.totalParcelado)
    ? product.details.totalParcelado[1]
    : product.details.totalParcelado

  const installmentPriceWithInterest = details.installmentPriceWithInterest ?? 0

  return {
    color,
    colorsQuantity,
    de: product.oldPrice,
    hasVariablePrice:
      product.has_variable_price ??
      product.details?.possuiPrecoVariavel ??
      false,
    id: +product.id,
    imageUrl: product.images.default,
    installmentPriceWithInterest,
    installmentMaxQuantity: product?.installment?.count,
    installmentMaxQuantityWithoutInterest:
      product?.details?.numeroParcelasSemJuros,
    isAvailable: product.status.toUpperCase() === AVAILABLE,
    percent: product.details.percentualDesconto,
    showDiscountBadge: product.showDiscountBadge,
    showPromoBadge: product.showPromoBadge,
    por: validatedTotalParcelado,
    price: product.price,
    promoEventImage: {
      desktop: product.images?.promoEventDesktop,
      mobile: product.images?.promoEventMobile,
    },
    productUrl,
    reviewQuantity: details.quantidadeAvaliacoes ?? 0,
    reviewRating: details.mediaAvaliacoes ?? 0,
    title: product.name,
    trackingUrl: product.trackingUrl,
    voltage,
  }
}

const makeSlugFromDetails = (
  details: any,
  slugTarget: string,
  valueTarget: string
): ProductCombination => {
  return {
    slug: details[slugTarget]?.toString() || '',
    valor: details[valueTarget]?.toString() || '',
  }
}

export const formatVoltage = (voltage: string, label = '-volts') => {
  const treatedVoltage = voltage.replace(/\D/g, '')

  if (treatedVoltage) return `${treatedVoltage}${label}`

  return voltage
}

export const colorAndVoltageStructure = (product: ProductProps): string => {
  return [
    isValidColorBySlug(product.color?.slug) ? product.color.valor : undefined,
    isValidVoltageBySlug(product.voltage?.slug)
      ? formatVoltage(product.voltage.valor, ' Volts')
      : undefined,
  ]
    .filter(Boolean)
    .join(' | ')
}

export const getProductColorsForGA = (product: ProductApi) => {
  return (
    product.skus?.map((sku) => {
      if (sku.specs.cor) {
        const color = Array.isArray(sku.specs.cor)
          ? sku.specs.cor[0]
          : sku.specs.cor

        return [{ label: color }]
      }
      return []
    }) || []
  )
}
